/**
 * `useTableFilters` — URL-state for the candidates table filters + sort.
 *
 * URL is the single source of truth (design §4.3). Params:
 *   - `?status=<one of 8>` — single-value status filter.
 *   - `?q=<substring>`     — case-insensitive name substring.
 *   - `?sort=<key>`        — sort column (default `rank`).
 *   - `?dir=<asc|desc>`    — sort direction (default `asc`).
 *
 * The hook does NOT mutate `?jd=` — that's the page's responsibility (shared
 * with Dashboard via `<JdSelector>`).
 */
import { useSearchParams } from "react-router-dom";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { SortDir, SortKey } from "./types";

const VALID_STATUSES: ReadonlySet<CandidateStatus> = new Set<CandidateStatus>([
  "active",
  "phone_screen",
  "interview_scheduled",
  "interviewed",
  "offer",
  "hired",
  "rejected",
  "opted_out",
]);

const VALID_SORT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>([
  "rank",
  "name",
  "status",
  "fit",
  "tenure",
]);

interface TableFilterState {
  status: CandidateStatus | null;
  q: string;
  sortKey: SortKey;
  sortDir: SortDir;
  setStatus: (s: CandidateStatus | null) => void;
  setQ: (q: string) => void;
  setSort: (k: SortKey, d: SortDir) => void;
  /**
   * Toggle sort: clicking the current sort column flips direction; clicking a
   * different column resets to that column ascending. Header cells call this.
   */
  toggleSort: (k: SortKey) => void;
}

function parseStatus(raw: string | null): CandidateStatus | null {
  if (!raw) return null;
  return VALID_STATUSES.has(raw as CandidateStatus)
    ? (raw as CandidateStatus)
    : null;
}

function parseSortKey(raw: string | null): SortKey {
  if (!raw) return "rank";
  return VALID_SORT_KEYS.has(raw as SortKey) ? (raw as SortKey) : "rank";
}

function parseSortDir(raw: string | null): SortDir {
  return raw === "desc" ? "desc" : "asc";
}

export function useTableFilters(): TableFilterState {
  const [params, setParams] = useSearchParams();
  const status = parseStatus(params.get("status"));
  const q = params.get("q") ?? "";
  const sortKey = parseSortKey(params.get("sort"));
  const sortDir = parseSortDir(params.get("dir"));

  // Mutators preserve all other params (notably `?jd=`).
  const setStatus = (s: CandidateStatus | null): void => {
    const next = new URLSearchParams(params);
    if (s) next.set("status", s);
    else next.delete("status");
    setParams(next, { replace: true });
  };

  const setQ = (nextQ: string): void => {
    const next = new URLSearchParams(params);
    if (nextQ) next.set("q", nextQ);
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const setSort = (k: SortKey, d: SortDir): void => {
    const next = new URLSearchParams(params);
    next.set("sort", k);
    next.set("dir", d);
    setParams(next, { replace: true });
  };

  const toggleSort = (k: SortKey): void => {
    if (k === sortKey) {
      setSort(k, sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(k, "asc");
    }
  };

  return { status, q, sortKey, sortDir, setStatus, setQ, setSort, toggleSort };
}
